import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import {
  getMyLetters, getMyLetterRequests, getRequestableLetterTypes, createLetterRequest,
  getLetterRequests, approveLetterRequest, rejectLetterRequest, downloadLetterPdf,
  signLetter, declineLetterSignature,
} from "../api";
import { SignaturePad } from "../components/SignaturePad";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  pending:  { bg: "#fef3c7", fg: "#b45309" },
  approved: { bg: "#dcfce7", fg: "#15803d" },
  rejected: { bg: "#fee2e2", fg: "#b91c1c" },
};

function StatusChip({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || { bg: "#f1f5f9", fg: "#475569" };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function LettersPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const isHR = ["admin", "admin_assistant", "hr"].includes(user?.role || "");

  const [tab, setTab] = useState<"my" | "queue">("my");
  const [myLetters, setMyLetters] = useState<any[]>([]);
  const [myRequests, setMyRequests] = useState<any[]>([]);
  const [queue, setQueue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Request modal
  const [showRequest, setShowRequest] = useState(false);
  const [types, setTypes] = useState<{ value: string; label: string }[]>([]);
  const [reqType, setReqType] = useState("");
  const [reqNote, setReqNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reject modal
  const [rejecting, setRejecting] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionId, setActionId] = useState<number | null>(null);
  const { downloading, download } = useBlockingDownload();

  // Sign modal (employee acknowledging a letter)
  const [signing, setSigning] = useState<any | null>(null);
  const [signTypedName, setSignTypedName] = useState("");
  const [signDrawn, setSignDrawn] = useState<string | null>(null);
  const [signConsent, setSignConsent] = useState(false);
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [declining, setDeclining] = useState<any | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  async function load() {
    try {
      const [letters, requests] = await Promise.all([getMyLetters(), getMyLetterRequests()]);
      setMyLetters(letters);
      setMyRequests(requests);
      if (isHR) setQueue(await getLetterRequests());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { getRequestableLetterTypes().then(setTypes).catch(() => {}); }, []);

  async function handleSubmitRequest() {
    if (!reqType) { toast("Select a letter type", "error"); return; }
    setSubmitting(true);
    try {
      await createLetterRequest({ letter_type: reqType, note: reqNote.trim() || undefined });
      toast("Letter request submitted", "success");
      setShowRequest(false); setReqType(""); setReqNote("");
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to submit request", "error");
    } finally { setSubmitting(false); }
  }

  async function handleDownload(letterId: number) {
    await download(() => downloadLetterPdf(letterId), "Download failed");
  }

  function openSign(letter: any) {
    setSigning(letter);
    setSignTypedName("");
    setSignDrawn(null);
    setSignConsent(false);
  }

  async function handleSign() {
    if (!signing) return;
    setSignSubmitting(true);
    try {
      await signLetter(signing.id, signTypedName.trim(), signDrawn, signConsent);
      toast("Letter signed ✓", "success");
      setSigning(null);
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to sign", "error");
    } finally { setSignSubmitting(false); }
  }

  async function handleDeclineSignature() {
    if (!declining) return;
    setSignSubmitting(true);
    try {
      await declineLetterSignature(declining.id, declineReason.trim());
      toast("Signature declined", "info");
      setDeclining(null); setDeclineReason("");
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to decline", "error");
    } finally { setSignSubmitting(false); }
  }

  async function handleApprove(req: any) {
    setActionId(req.id);
    try {
      await approveLetterRequest(req.id);
      toast(`${req.letter_type_label} generated & issued for ${req.employee_name}`, "success");
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Approve failed", "error");
    } finally { setActionId(null); }
  }

  async function handleReject() {
    if (!rejecting) return;
    setActionId(rejecting.id);
    try {
      await rejectLetterRequest(rejecting.id, rejectReason.trim());
      toast("Request declined", "success");
      setRejecting(null); setRejectReason("");
      load();
    } catch { toast("Failed", "error"); }
    finally { setActionId(null); }
  }

  const pendingCount = queue.filter(r => r.status === "pending").length;

  return (
    <div>
      {/* Width and padding come from ShellMain. This is a list page like
          Candidates, Jobs, Offers and Talent Pool — all full width — so a
          cap here left it visibly narrower than its siblings, and its own
          padding stacked on the shell's. */}
      <LoadingOverlay active={submitting || actionId !== null || downloading || signSubmitting} />
      {/* The house `.page-header` block. A hand-rolled navy <h2> here read as a
          different kind of heading from the black <h1> every sibling page uses. */}
      <div className="page-header">
        <div>
          <h1>Letters</h1>
          <p>Request HR letters and download issued ones</p>
        </div>
        <button onClick={() => setShowRequest(true)}
          style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
          + Request a Letter
        </button>
      </div>

      {isHR && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <button onClick={() => setTab("my")} style={{
            padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13,
            background: tab === "my" ? NAVY : "#f1f5f9", color: tab === "my" ? "white" : "#475569",
          }}>My Letters</button>
          <button onClick={() => setTab("queue")} style={{
            padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13,
            background: tab === "queue" ? NAVY : "#f1f5f9", color: tab === "queue" ? "white" : "#475569",
          }}>
            Employee Requests{pendingCount > 0 && <span style={{ marginLeft: 6, background: "#ef4444", color: "white", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{pendingCount}</span>}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : tab === "queue" && isHR ? (
        /* ── HR queue ── */
        queue.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>No letter requests yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {queue.map(r => (
              <div key={r.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
                    {r.letter_type_label} — {r.employee_name} {r.employee_id_code ? `(${r.employee_id_code})` : ""}
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {r.note ? <>Purpose: {r.note} · </> : null}
                    Requested {formatDate(r.created_at, "short", tz)}
                  </div>
                  {r.status === "rejected" && r.reject_reason && (
                    <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>Reason: {r.reject_reason}</div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <StatusChip status={r.status} />
                  {r.status === "pending" && (
                    <>
                      <button disabled={actionId === r.id} onClick={() => handleApprove(r)}
                        style={{ background: "#16a34a", color: "white", border: "none", padding: "8px 16px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                        {actionId === r.id ? "Generating…" : "Approve & Generate"}
                      </button>
                      <button disabled={actionId === r.id} onClick={() => { setRejecting(r); setRejectReason(""); }}
                        style={{ background: "white", color: "#b91c1c", border: "1.5px solid #fca5a5", padding: "8px 14px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                        Decline
                      </button>
                    </>
                  )}
                  {r.status === "approved" && r.letter_id && (
                    <AsyncButton onClick={() => handleDownload(r.letter_id)}
                      style={{ background: "white", color: NAVY, border: `1.5px solid ${NAVY}`, padding: "8px 14px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                      ⬇ PDF
                    </AsyncButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* ── Employee view ── */
        <>
          {myRequests.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: 15, color: "#334155", marginBottom: 12 }}>My Requests</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {myRequests.map(r => (
                  <div key={r.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{r.letter_type_label}</span>
                      <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 10 }}>
                        {formatDate(r.created_at, "short", tz)}
                      </span>
                      {r.status === "rejected" && r.reject_reason && (
                        <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>Reason: {r.reject_reason}</div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <StatusChip status={r.status} />
                      {r.status === "approved" && r.letter_id && (
                        <AsyncButton onClick={() => handleDownload(r.letter_id)}
                          style={{ background: NAVY, color: "white", border: "none", padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                          ⬇ Download
                        </AsyncButton>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h3 style={{ fontSize: 15, color: "#334155", marginBottom: 12 }}>Issued Letters</h3>
          {myLetters.length === 0 ? (
            <div style={{ textAlign: "center", padding: 50, color: "#94a3b8", background: "#f8fafc", borderRadius: 12 }}>
              <div style={{ fontSize: 40 }}>📄</div>
              <div style={{ marginTop: 10 }}>No letters issued to you yet. Use "Request a Letter" above.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
              {myLetters.map(l => (
                <div key={l.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>{l.letter_type_label}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
                    Issued {formatDate(l.issued_at, "short", tz)}
                  </div>
                  {l.needs_signature && l.signature_status === "pending" && (
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 12, color: "#b45309", fontWeight: 600 }}>
                      ⏳ Your signature is requested
                    </div>
                  )}
                  {l.needs_signature && l.signature_status === "signed" && (
                    <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 12, color: "#166534", fontWeight: 600 }}>
                      ✓ Signed
                    </div>
                  )}
                  {l.needs_signature && l.signature_status === "pending" ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => openSign(l)}
                        style={{ flex: 1, background: `linear-gradient(135deg,${NAVY},${TEAL})`, color: "white", border: "none", padding: "8px 12px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                        ✍️ Sign
                      </button>
                      <button onClick={() => { setDeclining(l); setDeclineReason(""); }}
                        style={{ background: "white", color: "#dc2626", border: "1.5px solid #fecaca", padding: "8px 12px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                        Decline
                      </button>
                    </div>
                  ) : (
                    <AsyncButton onClick={() => handleDownload(l.id)}
                      style={{ background: NAVY, color: "white", border: "none", padding: "8px 16px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 12, width: "100%" }}>
                      ⬇ Download PDF
                    </AsyncButton>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Request modal ── */}
      {showRequest && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 440, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 16px", color: NAVY }}>Request a Letter</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Letter type</label>
            <select value={reqType} onChange={e => setReqType(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 16 }}>
              <option value="">— select —</option>
              {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Purpose (optional)</label>
            <textarea value={reqNote} onChange={e => setReqNote(e.target.value)} rows={3}
              placeholder="e.g. visa application, bank loan…"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 20, resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowRequest(false)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={submitting} onClick={handleSubmitRequest}
                style={{ background: NAVY, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject modal ── */}
      {rejecting && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 8px", color: NAVY }}>Decline Request</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px" }}>
              {rejecting.letter_type_label} for {rejecting.employee_name}
            </p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
              placeholder="Reason (shown to the employee)…"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 18, resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setRejecting(null)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={actionId === rejecting.id} onClick={handleReject}
                style={{ background: "#dc2626", color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sign modal (employee acknowledgement) ── */}
      {signing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 460, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 8px", color: NAVY }}>Sign: {signing.letter_type_label}</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
              This applies your digital signature to acknowledge this letter.
            </p>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Type your full name</label>
            <input value={signTypedName} onChange={e => setSignTypedName(e.target.value)} placeholder="Your full legal name"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }} />
            <SignaturePad onChange={setSignDrawn} />
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#475569", marginTop: 12, marginBottom: 18, cursor: "pointer" }}>
              <input type="checkbox" checked={signConsent} onChange={e => setSignConsent(e.target.checked)} style={{ marginTop: 2 }} />
              I agree that clicking below constitutes my electronic signature on this document.
            </label>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setSigning(null)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={signSubmitting || !signTypedName.trim() || !signConsent} onClick={handleSign}
                style={{ background: (!signTypedName.trim() || !signConsent) ? "#94a3b8" : `linear-gradient(135deg,${NAVY},${TEAL})`, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: (!signTypedName.trim() || !signConsent) ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13 }}>
                {signSubmitting ? "Signing…" : "✍️ Sign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Decline signature modal ── */}
      {declining && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 8px", color: NAVY }}>Decline Signature</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px" }}>{declining.letter_type_label}</p>
            <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3}
              placeholder="Reason (shown to HR)…"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 18, resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeclining(null)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={signSubmitting || !declineReason.trim()} onClick={handleDeclineSignature}
                style={{ background: "#dc2626", color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
